import { Injectable, isDevMode } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AppConfig } from '../app.config';
import { context } from 'bookingbug-core-js';

@Injectable({
  providedIn: 'root',
})
export class CapitaService {
  private core = AppConfig.settings.core;
  private apiServer = AppConfig.settings.apiServer;
  //private url =
  // window.location.hostname == "localhost"
  //   ? "http://localhost:4201"
  // : "https://staging.bookinglab.co.uk";
  private url = 'https://staging.bookinglab.co.uk';
  // private url = 'http://localhost:4201';
  private path = "/";
  capitaUrlPath =
    window.location.hostname.indexOf('staging') !== -1
      ? '/payment/'
      : '/payment/';

  constructor(private http: HttpClient) {}

  async invoke(
    company,
    booking,
    purchase,
    client,
    description,
    fundCode,
    price,
    vatOptOut,
    ref,
    extraItemBonuses
  ) {
    if (!fundCode) {
      fundCode = 'MISC';
    }

    let port = '';
    // '' is the default port for the scheme -- without it a plain https
    // page scored a stray ":" into the payment gateway URL.
    const ports: any = ['80', '443', ''];
    if (ports.indexOf(window.location.port) === -1) {
      port = `:${window.location.port}`;
    }

    const body = {
      host: `${context.apiUrl}`,
      company_id: `${company.id}`,
      booking_id: `${booking.id}`,
      purchase_id: `${purchase.id}`,
      client_id: `${client.id}`,
      fund_code: `${fundCode}`,
      description: `${description}`,
      reference: `${ref}`,
      amount: price,
      send_user_details: false, // this adds the billingDetails element to the Capita XML with the card holder name and email pre-populated in the portal
      combine_reference: true,
      add_account_details: true, // This adds the accountName field to the lgItemDetails section of the Capita XML
      vat_opt_out: vatOptOut,
      do_not_store_ref: true,
      return_url: `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify?p=${purchase.id}&b=${booking.id}&c=${company.id}`,
      back_url: `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify?p=${purchase.id}&b=${booking.id}&c=${company.id}`,
    };

    if (extraItemBonuses) {
      body['additionalCharges'] = [];
      for (let extraItemBonus of extraItemBonuses) {
        body['additionalCharges'].push({
          chargeName: extraItemBonus.item_name,
          charge: extraItemBonus.chargePrice * 100,
          glCode: extraItemBonus.data.short_code,
        });
        body['basePrice'] = price - extraItemBonus.chargePrice * 100;
      }
    }

    return this.http
      .post(`${this.url}${this.capitaUrlPath}capita/invoke`, body)
      .toPromise();
  }

  // Ported from the previous Solihull appointments app (SRPO-169): builds the
  // payment body consumed by civicaEstore() from JRNI bookings/purchases.
  bodyMaker(
    company,
    bookings,
    purchase,
    client,
    service,
    fundCode,
    vatOptOut,
    itemsWithCatalog
  ) {
    if (!fundCode) {
      fundCode = 'MISC';
    }

    let booking_ids = '';
    let description = '';
    let purchase_ids = '';
    let price = '';
    let location_ids = '';
    let catalogue_ids = '';
    let qty = '';

    for (const i in bookings) {
      booking_ids += bookings[i].id + ',';
      description += bookings[i].service_name + ',';
      purchase_ids += bookings[i].purchase_id + ',';
      price += itemsWithCatalog[i].price + ',';
      location_ids += bookings[i].company_id + ',';
      catalogue_ids += itemsWithCatalog[i].catalogue_id + ',';
      qty += itemsWithCatalog[i].quantity + ',';
    }
    // remove trailing comma
    booking_ids = booking_ids.substring(0, booking_ids.length - 1);
    description = description.substring(0, description.length - 1);
    purchase_ids = purchase_ids.substring(0, purchase_ids.length - 1);
    price = price.substring(0, price.length - 1);
    location_ids = location_ids.substring(0, location_ids.length - 1);
    catalogue_ids = catalogue_ids.substring(0, catalogue_ids.length - 1);
    qty = qty.substring(0, qty.length - 1);

    let port = '';
    // '' is the default port for the scheme -- without it a plain https
    // page scored a stray ":" into the payment gateway URL.
    const ports: any = ['80', '443', ''];
    if (ports.indexOf(window.location.port) === -1) {
      port = `:${window.location.port}`;
    }

    const body = {
      host: `${context.apiUrl}`,
      company_id: `${location_ids}`,
      booking_id: `${booking_ids}`,
      purchase_id: `${purchase_ids}`,
      client_id: `${client.id}`,
      fund_code: `${fundCode}`,
      description: `${description}`,
      reference: `${AppConfig.settings.payment.reference}`,
      amount: parseInt(price) / 100,
      send_user_details: false,
      combine_reference: true,
      vat_opt_out: vatOptOut,
      return_url: `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify`,
      back_url: `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify`,
      catalogue_ids: catalogue_ids,
      quantity: qty,
    };

    return body;
  }

  // Civica eStore redirect (SRPO-169): returns the gateway URL the browser is
  // sent to after the booking has been created post-basket. The eStore backend
  // redirects back to /payment-verify with ref/ResponseCode/BasketRef params.
  async civicaEstore(body) {
    const params =
      `?` +
      `host=${body.host}` +
      `&description=${body.description}` +
      `&amount=${body.amount}` +
      `&quantity=${body.quantity}` +
      `&return_url=${body.return_url}` +
      `&booking_id=${body.booking_id}` +
      `&company_id=${body.company_id}` +
      `&client_id=${body.client_id}` +
      `&purchase_id=${body.purchase_id}` +
      `&cancel_url=${body['cancel_url']}` +
      `&email=${body['email']}` +
      `&phone=${body['phone']}` +
      `&confirm_id=${body['confirm_id']}` +
      `&vat_opt_out=${body['vat_opt_out']}` +
      `&catalogue_ids=${body.catalogue_ids}`;
    return `${this.url}/payment/civica/estore/` + params;
  }

  async preBasketInvoke(company, service, basket, serviceItems, descriptions, client, fundCode, amounts, authToken, data, detailGroupId, slot, extraDuration, journeyRoute, reference) {
    let port = '';
    // '' is the default port for the scheme -- without it a plain https
    // page scored a stray ":" into the payment gateway URL.
    const ports: any = ['80', '443', ''];
    console.log('port', window.location.port);
    if (ports.indexOf(window.location.port) === -1) {
      port = `:${window.location.port}`;
    }

    let serviceItemString = '';
    for (const serviceItem of serviceItems) {
      serviceItemString = `${serviceItemString},${serviceItem.id}`;
    }

    console.log(`Journey route: ${journeyRoute}`);

    let returnUrl = '';
    if (journeyRoute == "order_a_copy_certificate") {
      returnUrl = `${window.location.protocol}//${window.location.hostname}${port}/start/${journeyRoute}/verify/${company.id}/${basket.id}/${serviceItemString}`;
    } else {
      returnUrl = `${window.location.protocol}//${window.location.hostname}${port}${this.path}payment-verify/${company.id}/${basket.id}/${serviceItemString}`;
    }

    console.log(`journeyRoute: ${journeyRoute} Return URL: ${returnUrl}`);

    const body = {
      host: `${context.apiUrl}`,
      // host: 'https://westminster-staging.council.jrni.com',
      company_id: `${company.id}`,
      child_company: `${company.id}`,
      service_id: `${service.id}`,
      client_id: `${client.id}`,
      basket: `${basket.id}`,
      service_items: serviceItems,
      descriptions: descriptions,
      // amount: amounts,
      data: data,
      return_url: `${returnUrl}`,
      back_url: `${returnUrl}`,
      auth_token: `${authToken}`,
      detail_group: detailGroupId,
      slot: slot,
      extra_duration: extraDuration,
      journey_route: journeyRoute,
      reference: `${reference}`,
      add_account_details: false,
      displayableReference: `${descriptions[0]}`
    }
    console.log(typeof fundCode, Array.isArray(fundCode));
    if (Array.isArray(amounts)) {
      body['amounts'] = amounts;
    }
    else {
      body['amount'] = `${amounts}`;
    }
    if (Array.isArray(fundCode)) {
      body['fund_codes'] = fundCode;
    }
    else {
      body['fund_code'] = `${fundCode}`;
    }

    return this.http.post(`${this.url}${this.capitaUrlPath}capita/pre-basket`, body).toPromise();
  }

  async preBasketFind(companyId, basketId, serviceItemId) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
    };
    return this.http.post(`${this.url}${this.capitaUrlPath}capita/pre-basket/find`, body).toPromise();
  }

  async preBasketUpdate(companyId, basketId, serviceItemId, status, ref, data) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      child_company: `${companyId}`,
      basket: `${basketId}`,
      service_item: `${serviceItemId}`,
      ref: ref,
      status: `${status}`,
      data: data
    };
    return this.http.post(`${this.url}${this.capitaUrlPath}capita/pre-basket/update`, body).toPromise();
  }

  async query(paymentRef) {
    const body = {
      host: `${context.apiUrl}`,
      scp_id: paymentRef,
    };
    return this.http
      .post(`${this.url}${this.capitaUrlPath}capita/query`, body)
      .toPromise();
  }

  async find(purchaseId, companyId) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      purchase_id: `${purchaseId}`,
    };

    return this.http
      .post(`${this.url}${this.capitaUrlPath}capita/find`, body)
      .toPromise();
  }

  async confirm(purchaseId, companyId, amount) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      purchase_id: `${purchaseId}`,
      amount: amount,
    };

    return this.http
      .post(`${this.url}${this.capitaUrlPath}capita/confirm`, body)
      .toPromise();
  }

  async vatExemption(companyId, serviceItems) {
    const body = {
      host: `${context.apiUrl}`,
      company_id: `${companyId}`,
      service_items: serviceItems,
    };
    return this.http
      .post(`${this.url}${this.capitaUrlPath}capita/vat-exemption`, body)
      .toPromise();
  }
}
